import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { fetchDepartments, fetchDoctors, createAppointment } from '../../services/apiService';
import ToastService from '../../services/toastService';

const AppointmentForm = () => {
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  const selectedDepartment = watch('departmentId'); // Watch department value

  const onSubmit = async (data) => {
    const selectedDoctor = doctors.find(doc => doc._id === data.doctorId);
    const submissionData = {
      ...data,
      isGuestUser: true,
      patientPhone: '+91'+data.patientPhone.toString(), // Ensure phone is a string
      doctorName: selectedDoctor?.name || '',
    };

    setLoading(true);
		try {
			const response = await createAppointment(submissionData);
			if (response.status === 201) {
				ToastService.success(response.data.message);
				reset();
			} else {
				ToastService.error(response.data.message || "Failed to send message");
			}
		} catch (error) {
			ToastService.error(error?.response?.data?.message);
		}finally {
			setLoading(false);
		}
  };

  const getDepartments = async () => {
    setFetching(true);
    try {
      const response = await fetchDepartments();
      if (response.status === 200) {
        setDepartments(response?.data?.data || []);
      } else {
        console.error("Failed to fetch departments:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    } finally {
      setFetching(false);
    }
  };

  const getDoctors = async (departmentId) => {
    if (!departmentId) return;

    setFetching(true);
    try {
      const response = await fetchDoctors(departmentId);
      if (response.status === 200) {
        setDoctors(response?.data?.data || []);
      } else {
        console.error("Failed to fetch doctors:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching doctors:", error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    getDepartments();
  }, []);
  useEffect(() => {
    if (selectedDepartment) {
      getDoctors(selectedDepartment);
    } else {
      setDoctors([]); // Clear doctors if no department selected
    }
  }, [selectedDepartment]);

  return (
    <div className="appointment-form form-wraper">
      <h3 className="title">Book Appointment</h3>
      <p>
        Choose your desired department and doctor to receive the care you
        require. Please provide your information below, and we will verify your
        appointment at our earliest convenience.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ position: "relative" }}>
        {/* Department */}
        <div className='h-100' style={{ display: fetching ? 'block' : 'none', position: 'absolute', inset: '0px', background: 'rgb(255 255 255 / 66%)', zIndex: '1' }}>
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ position: 'absolute', top: '45%', left: '45%' }}></span>
        </div>
        <div className='row'>
          <div className='col-md-6'>
            <div className="form-group text-start">
              <i className="fas fa-chevron-down"></i>
              <select
                className="form-select form-control"
                {...register('departmentId', { required: 'Department is required' })}
              >
                <option value="">Select Department</option>
                {departments.length > 0 && departments.map((department) => (
                  <option key={department._id} value={department._id}>
                    {department.name}
                  </option>
                ))}
              </select>
              {errors.departmentId && (
                <span className="text-danger small">{errors.departmentId.message}</span>
              )}
            </div>
          </div>
          <div className='col-md-6'>
            <div className="form-group text-start">
              <i className="fas fa-chevron-down"></i>
              <select
                className="form-select form-control"
                disabled={!selectedDepartment} // Disable if no department selected
                {...register('doctorId', { required: 'Doctor is required' })}
              >
                <option value="">Select Doctor</option>
                {doctors.length > 0 ? doctors.map((doctor) => (
                  <option key={doctor._id} value={doctor._id}>
                    {doctor.name}
                  </option>
                )) : <option value={""} disabled>No doctors available in selected department</option>}
              </select>
              {errors.doctorId && (
                <span className="text-danger small">{errors.doctorId.message}</span>
              )}
            </div>
          </div>
          <div className='col-md-6'>
            <div className="form-group text-start">
              <input
                type="text"
                className="form-control"
                placeholder="Your Name"
                {...register('patientName', { required: 'Name is required' })}
              />
              {errors.patientName && (
                <span className="text-danger small">{errors.patientName.message}</span>
              )}
            </div>
          </div>
          <div className='col-md-6'>
            <div className="form-group text-start">
              <input
                type="number"
                className="form-control"
                placeholder="Phone Number"
                {...register('patientPhone', {
                  required: 'Phone number is required',
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: 'Enter a valid 10-digit number',
                  },
                })}
              />
              {errors.patientPhone && (
                <span className="text-danger small">{errors.patientPhone.message}</span>
              )}
            </div>
          </div>
          <div className='col-md-12'>
            <div className="form-group text-start">
              <input
                type="email"
                className="form-control"
                placeholder="Email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: "Invalid email address"
                  }
                })}
              />
              {errors.email && <small className="text-danger">{errors.email.message}</small>}
            </div>
          </div>
          <div className='col-md-6'>
            <div className="form-group text-start">
              <input
                type="date"
                className="form-control"
                {...register('date', { required: 'Date is required' })}
              />
              {errors.date && (
                <span className="text-danger small">{errors.date.message}</span>
              )}
            </div>
          </div>
          <div className='col-md-6'>
            <div className="form-group text-start">
              <i className="fas fa-chevron-down"></i>
              <select
                className="form-select form-control"
                {...register('appointmentType', { required: 'Appointment type is required' })}
              >
                <option value="">Select Appointment Type</option>
                <option value="Checkup">Checkup</option>
                <option value="Follow Up">Follow Up</option>
                <option value="Emergency">Emergency</option>
                <option value="Consultation">Consultation</option>
                <option value="Test">Test</option>
                <option value="Sick">Sick</option>
              </select>
              {errors.appointmentType && (
                <span className="text-danger small">{errors.appointmentType.message}</span>
              )}
            </div>
          </div>
          <div className='col-md-12  text-start'>
            <label className="form-label">Choose Time:</label>
            <div className="d-flex flex-wrap gap-2">
              {['9:00', '9:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00'].map((time, index) => (
                <React.Fragment key={index}>
                  <input
                    type="radio"
                    className="btn-check"
                    id={`option${index}`}
                    value={time}
                    {...register('startTime', { required: 'Time slot is required' })}
                  />
                  <label
                    className="btn btn-sm btn-timeSlot btn-outline-primary"
                    htmlFor={`option${index}`}
                  >
                    {time}
                  </label>
                </React.Fragment>
              ))}
            </div>
            {errors.startTime && (
              <span className="text-danger small">{errors.startTime.message}</span>
            )}
          </div>
          <div className='col-md-12'>
            <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
              {loading ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : 'Book Appointment'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AppointmentForm;
